import { Injectable, inject } from '@angular/core';
import { EnvironmentService } from 'projects/bis-shared/src/lib/environment/environment.service';
import { Environment } from 'projects/bis-shared/src/lib/environment/environment';
import {
  EntityTypeCode,
  ObjectTypeCode,
  SystemTypeCode,
} from 'projects/bis-shared/src/lib/utility/system-type-constants';
import { TransactionType } from 'projects/bis-shared/src/lib/utility/transaction-constants';

/**************************************
 *
 *  This naming convention is temporary
 *  until we can fully refactor apiurls
 *  into Internal and External
 *
 **************************************/

@Injectable({
  providedIn: 'root',
})
export class ExternalApiUrls {

  
 

  constructor() {
    const environmentService = inject(EnvironmentService);

    const config = environmentService.getEnvironment();

    // Try to keep this in alpha order to avoid dups
    this.address._initialize(config);
    this.businessCard._initialize(config);
    this.document._initialize(config);
    this.employer._initialize(config);
    this.filing._initialize(config);
    this.healthcareProvider._initialize(config);
    this.healthcareProviderLicenseSpecialty._initialize(config);
    this.healthcareProviderLicenseImpartialSpecialist._initialize(config);
    this.imeRequest._initialize(config);
    this.insurer._initialize(config);
    this.medicalBilling._initialize(config);
    this.medicalSupplier._initialize(config);
    this.medicalBillingDispute._initialize(config);
    this.mdoReviewRequest._initialize(config);
    this.priorAuthorization._initialize(config);
    this.postalCodePrepopulate._initialize(config);
    this.priorAuthorizationRequest._initialize(config);
    this.priorAuthorizationWorkRequest._initialize(config);
    this.priorAuthorizationResponse._initialize(config);
    this.thirdPartyAdministrator._initialize(config);
    this.user._initialize(config);
    this.userProfile._initialize(config);
    this.workRequest._initialize(config);
    this.workGroup._initialize(config);
    this.workRequest._initialize(config);
    this.sender._initialize(config);
  }

  employer = {
    get(id: number) {
      return `${this.employerUrl}/${id}`;
    },
    search() {
      return `${this.employerUrl}/search`;
    },
    _initialize(config: Environment) {
      this.employerUrl = config.baseUrls.entityApiUrl + '/api/external/employer';
    },
  };

  filing = {
    getFilingConfirmation(id: number) {
      return `${this.filingApiUrl}/confirmation/${id}`;
    },
    searchDraftEforms() {
      return `${this.filingApiUrl}/draftEforms`;
    },
    searchSubmittedEforms() {
      return `${this.filingApiUrl}/submittedEforms`;
    },
    getDraftEForm(filingId: number) {
      return `${this.filingApiUrl}/draftEForm/${filingId}`;
    },
    getEForm(filingId: number) {
      return `${this.filingApiUrl}/eForm/${filingId}`;
    },
    saveDraftEForm(documentFormId: number) {
      return `${this.filingApiUrl}/${documentFormId}/draftEForm`;
    },
    discardDraftEForm(filingId: number) {
      return `${this.filingApiUrl}/discardDraftEForm/${filingId}`;
    },
    getFilingSubmissionStatus(id: number) {
      return `${this.filingApiUrl}/submissionstatus/${id}`;
    },
    _initialize(config: Environment) {
      this.filingApiUrl = config.baseUrls.filingApiUrl + '/api/external/filing';
    },
  };

  healthcareProviderLicenseSpecialty = {
    search() {
      return `${this.healthcareProviderLicenseSpecialtyUrl}/search`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderLicenseSpecialtyUrl =
        config.baseUrls.entityApiUrl + '/api/external/healthcareProviderLicenseSpecialty';
    },
  };

  healthcareProviderLicenseImpartialSpecialist = {
    search() {
      return `${this.healthcareProviderLicenseImpartialSpecialistDesignationUrl}/search`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderLicenseImpartialSpecialistDesignationUrl =
        config.baseUrls.entityApiUrl +
        '/api/external/healthcareProviderLicenseImpartialSpecialistDesignation';
    },
  };

  address = {
    getAddresses() {
      return `${this.addressUrl}/search`;
    },
    _initialize(config: Environment) {
      this.addressUrl = config.baseUrls.entityApiUrl + '/api/external/address';
    },
  };

  businessCard = {
    get(entityTypeCode: string, id: number) {
      return `${this.businessCardUrl}/${entityTypeCode}/${id}`;
    },
    _initialize(config: Environment) {
      this.businessCardUrl = config.baseUrls.entityApiUrl + '/api/external/businessCard';
    },
  };

  imeRequest = {
    canRequestIme(id: number) {
      return `${this.imeRequestUrl}/${id}`;
    },
    submitImeRequest() {
      return `${this.imeRequestUrl}`;
    },
    _initialize(config: Environment) {
      this.imeRequestUrl = config.baseUrls.medicalApiUrl + '/api/external/imerequest';
    },
  };

  medicalBillingDispute = {
    get(id: number) {
      return `${this.medicalBillingDisputeUrl}/api/medicalBillingDispute/${id}`;
    },
    _initialize(config: Environment) {
      this.medicalBillingDisputeUrl = config.baseUrls.medicalApiUrl + '/api/external';
    },
  };

  medicalBilling = {
    preview() {
      return `${this.medicalBillingApiUrl}/medicalbilling/HP1/Preview`;
    },
    submit() {
      return `${this.medicalBillingApiUrl}/medicalbilling/HP1`;
    },
    possibleDuplicates() {
      return `${this.medicalBillingApiUrl}/MedicalBillingDispute/duplicates`;
    },
    _initialize(config: Environment) {
      this.medicalBillingApiUrl = config.baseUrls.medicalApiUrl + '/api/external';
    },
  };

  mdoReviewRequest = {
    request() {
      return this.mdoReviewRequestApiUrl;
    },
    _initialize(config: Environment) {
      this.mdoReviewRequestApiUrl = `${config.baseUrls.medicalApiUrl}/api/external/mdoReviewRequest`;
    },
  };

  newPriorAuthApiUrl: string;
  priorAuthRequestApiUrl: string;

  priorAuthorizationRequest = {
    confirmation: {
      preview: () => `${this.newPriorAuthApiUrl}/consistent/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/consistent/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/consistent`,
    },
    medication: {
      preview: () => `${this.newPriorAuthApiUrl}/medication/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/medication/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/medication`,
    },
    durableMedicalEquipment: {
      preview: () => `${this.newPriorAuthApiUrl}/durableMedicalEquipment/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/durableMedicalEquipment/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/durableMedicalEquipment`,
    },
    nonMtgOver1000: {
      preview: () => `${this.newPriorAuthApiUrl}/nonMtgOver1000/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/nonMtgOver1000/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/nonMtgOver1000`,
    },
    nonMtgUnder1000: {
      preview: () => `${this.newPriorAuthApiUrl}/nonMtgUnder1000/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/nonMtgUnder1000/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/nonMtgUnder1000`,
    },
    specialServices: {
      preview: () => `${this.newPriorAuthApiUrl}/specialServices/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/specialServices/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/specialServices`,
    },
    variance: {
      preview: () => `${this.newPriorAuthApiUrl}/variance/preview`,
      readyToSubmit: () => `${this.newPriorAuthApiUrl}/variance/readyToSubmit`,
      submit: () => `${this.newPriorAuthApiUrl}/variance`,
    },
    support: {
      checkForExistingMtgPar: () => `${this.newPriorAuthApiUrl}/existing-mtg-par`,
      checkForExistingSpecialServicePar: () =>
        `${this.newPriorAuthApiUrl}/existing-special-services-par`,
    },
    _initialize: (config: Environment) => {
      this.newPriorAuthApiUrl =
        config.baseUrls.medicalApiUrl + '/api/external/priorAuthorizationRequest';
      this.priorAuthRequestApiUrl =
        config.baseUrls.medicalApiUrl + '/api/external/priorAuthorizationRequest';
    },
  };

  priorAuthorizationWorkRequest = {
    bulkUpdate() {
      return `${this.medicalApiUrl}/priorauthorization/bulkedit`;
    },
    _initialize(config: Environment) {
      this.medicalApiUrl = config.baseUrls.medicalApiUrl + '/api/external';
    },
  };

  priorAuthorization = {
    get(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${id}`;
    },
    getRelatedRequests(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedPriorAuthRequests/${priorAuthId}`;
    },
    healthcareProviderPriorAuth(healthcareProviderId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/healthcareProviderPriorAuth/${healthcareProviderId}`;
    },
    getMdoReviewType(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/mdoReviewType/${id}`;
    },
    durableMedicalEquipmentLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/durable-medical-equipment`;
    },
    durableMedicalEquipmentSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/durable-medical-equipment-with-responses`;
    },
    medicationLineItem(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/medication`;
    },
    medicationSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/medication-with-responses`;
    },
    mtgLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/mtg`;
    },
    mtgSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/mtg-with-responses`;
    },
    convertToVariance(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/convert-to-variance`;
    },
    nonMtgSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/non-mtg-with-responses`;
    },
    nonMtgLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/non-mtg`;
    },
    specialServiceLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/special-service`;
    },
    specialServiceSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/special-service-with-responses`;
    },
    correspondenceHistory(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${id}/correspondence-history`;
    },
    varianceDraftFromMtgSelections() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationrequest/variance/draft/frommtgselections`;
    },
    priorAuthorizationWorkRequest() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/priorAuthorizationWorkRequest`;
    },
    priorAuthorizationWorkRequestForInsurers() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/priorAuthorizationWorkRequestForInsurer`;
    },
    priorAuthorizationWorkRequestLevel() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/priorAuthorizationWorkRequestLevel`;
    },
    priorAuthorizationCompletedWorkRequest() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/completedpriorauths`;
    },
    priorAuthorizationWorkRequestAssignees() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/assignees`;
    },
    assignPriorAuthWorkRequest() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/assignworkrequest`;
    },
    getWorkloadAdminStatus() {
      return `${this.priorAuthorizationApiUrl}/priorauthorizationworkrequest/workloadadminstatus`;
    },
    getRelatedClaimant(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedClaimant/${id}`;
    },
    getRelatedHealthcareProvider(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedHealthcareProvider/${id}`;
    },
    getRelatedInsurer(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedInsurer/${id}`;
    },
    getPayerRelatedEntityGroup(priorAuthoizationId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/payerRelatedEntityGroup/${priorAuthoizationId}`;
    },
    getRelatedSenderEntity(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedSenderEntity/${id}`;
    },
    getRelatedClaimAdminEntity(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/relatedClaimAdminEntity`;
    },
    getRelatedSenderUsers(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedSenderUsers/${id}`;
    },
    addRelatedEntityUser(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedEntityUser/${id}`;
    },
    getOverallInsurerResponse(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/overall-insurer-response`;
    },
    updateRelatedEntityUser() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedEntityUser`;
    },
    removeAccessForAllRelatedUsers() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/removeAccessForAllRelatedUsers`;
    },
    submitProviderInformationResponse() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/providerInformationResponse`;
    },
    getRelatedUsersForNotification(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedEntityusersForNotification/${id}`;
    },
    uploadExternalPriorAuthDocument(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/uploadExternalPriorAuthDocument/${id}`;
    },
    saveL2Draft() {
      return `${this.priorAuthorizationApiUrl}/level2Request/draft`;
    },
    getL2Request(filingId: number) {
      return `${this.priorAuthorizationApiUrl}/level2Request/draft/${filingId}`;
    },
    readyToSubmitL2Review() {
      return `${this.priorAuthorizationApiUrl}/level2Request/readyToSubmit`;
    },
    submitL2Review() {
      return `${this.priorAuthorizationApiUrl}/level2Request/submit`;
    },
    submitProviderInformationRequest() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/providerinformationrequest`;
    },
    submitRequestInsurerReview() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/requestInsurerReview`;
    },
    submitUpdateClaimSender() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/updateClaimSender`;
    },
    getProviderInformationResponseDetails(priorAuthId: number, workRequestId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/providerInformationResponse/${priorAuthId}/${workRequestId}`;
    },
    checkUserCanSubmitPar() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/checkUserCanSubmitPar`;
    },
    priorAuthSearch() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/search`;
    },
    _initialize(config: Environment) {
      this.priorAuthorizationApiUrl = config.baseUrls.medicalApiUrl + '/api/external';
    },
    getSenderClaimAdmins(insurerId: string) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/getResponsibleEntitySendernums/${insurerId}`;
    },
    canRequestInsurerReview(priorAuthorizationId) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/canRequestInsurerReview/${priorAuthorizationId}`;
    },
  };

  userProfile = {
    get() {
      return `${this.userProfileUrl}`;
    },
    _initialize(config: Environment) {
      this.userProfileUrl = config.baseUrls.userApiUrl + '/api/external/userProfile';
    },
  };

  healthcareProvider = {
    get(id: number) {
      return `${this.healthcareProviderUrl}/${id}`;
    },
    licenses(healthcareProviderId: number) {
      return `${this.healthcareProviderUrl}/${healthcareProviderId}/licenses`;
    },
    updateProviderDelegateAllowed(healthcareProviderId: number, isProviderDelegate: boolean) {
      return `${this.healthcareProviderUrl}/${healthcareProviderId}/updateProviderDelegate/${isProviderDelegate}`;
    },
    getPrimaryZipCode(healthcareProviderId: number) {
      return `${this.healthcareProviderUrl}/${healthcareProviderId}/primary-zip-code`;
    },
    getOutOfState(healthcareProviderId: number) {
      return `${this.healthcareProviderUrl}/${healthcareProviderId}/out-of-state`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderUrl =
        config.baseUrls.entityApiUrl + '/api/external/healthcareprovider';
    },
  };

  insurer = {
    get(id: number) {
      return `${this.insurerUrl}/${id}`;
    },
    lookup() {
      return `${this.insurerUrl}/lookup`;
    },
    getInsurerGroups() {
      return `${this.insurerUrl}/insurergroups`;
    },
    _initialize(config: Environment) {
      this.insurerUrl = config.baseUrls.entityApiUrl + '/api/external/insurer';
    },
  };

  postalCodePrepopulate = {
    get(postalCode: string) {
      return `${this.postalCodePrepopulateUrl}/${postalCode}`;
    },
    _initialize(config: Environment) {
      this.postalCodePrepopulateUrl =
        config.baseUrls.refApiUrl + '/api/external/postalCodePrepopulate';
    },
  };

  priorAuthorizationResponse = {
    getDraft(filingId: number) {
      return `${this.priorAuthorizationResponseUrl}/draft/${filingId}`;
    },
    saveDraft() {
      return `${this.priorAuthorizationResponseUrl}/draft`;
    },
    preview() {
      return `${this.priorAuthorizationResponseUrl}/preview`;
    },
    submit() {
      return `${this.priorAuthorizationResponseUrl}`;
    },
    getDmeResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/dme/${objectTypeCode}`;
    },
    getMedicationResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/medication/${objectTypeCode}`;
    },
    getMtgResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/mtg/${objectTypeCode}`;
    },
    getNonMtgResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/nonMtg/${objectTypeCode}`;
    },
    getSpecialServiceResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/specialService/${objectTypeCode}`;
    },
    _initialize(config: Environment) {
      this.priorAuthorizationResponseUrl =
        config.baseUrls.medicalApiUrl + '/api/external/priorauthorizationresponse';
    },
  };

  medicalSupplier = {
    get(id: number) {
      return `${this.medicalSupplierUrl}/${id}`;
    },
    _initialize(config: Environment) {
      this.medicalSupplierUrl = config.baseUrls.entityApiUrl + '/api/external/medicalSupplier';
    },
  };

  thirdPartyAdministrator = {
    get(id: number) {
      return `${this.thirdPartyAdministratorUrl}/${id}`;
    },
    lookup() {
      return `${this.thirdPartyAdministratorUrl}/lookup`;
    },
    _initialize(config: Environment) {
      this.thirdPartyAdministratorUrl =
        config.baseUrls.entityApiUrl + '/api/external/thirdPartyAdministrator';
    },
  };

  user = {
    login() {
      return `${this.userUrl}/login/`;
    },
    logout() {
      return `${this.userUrl}/logout/`;
    },
    getExternalUserLookup() {
      return `${this.userUrl}/externalUserLookup`;
    },
    me() {
      return `${this.userUrl}/me/`;
    },
    userProfile() {
      return `${this.userUrl}/userProfile/`;
    },
    myGroups() {
      return `${this.userUrl}/myGroups`;
    },
    get(id: number) {
      return `${this.userUrl}/${id}`;
    },
    _initialize(config: Environment) {
      this.userUrl = config.baseUrls.userApiUrl + '/api/external/user';
    },
  };

  workRequest = {
    get(id: number) {
      return `${this.workRequestUrl}/${id}`;
    },
    getByObject(objectTypeCode: string, objectId: number) {
      return `${this.workRequestUrl}/object/${objectTypeCode}/${objectId}`;
    },
    getLatestObjectWorkRequest(objectTypeCode: string, objectId: number) {
      return `${this.workRequestUrl}/object/latest/${objectTypeCode}/${objectId}`;
    },
    getEntityWorkRequests(typeCode: string, entityId: number) {
      return `${this.workRequestUrl}/entity/${typeCode}/${entityId}`;
    },
    getConfiguration() {
      return `${this.workRequestUrl}/configuration`;
    },
    _initialize(config: Environment) {
      this.workRequestUrl = config.baseUrls.workflowApiUrl + '/api/external/workRequest';
    },
  };

  documentApiUrlBase: string;
  documentApiUrl: string;
  tokenApiUrl: string;

  document = {
    getDetails: (id: number): string => `${this.documentApiUrl}/${id}/externaldetails`,
    getRelatedByObjectId: () => `${this.documentApiUrl}/relatedbyObject`,
    getRelatedByEntityId: () => `${this.documentApiUrl}/relatedbyEntity`,
    getRelatedIdByObjectId: () => `${this.documentApiUrl}/relatedIdByObject`,
    getRelatedTagsByEntityId: () => `${this.documentApiUrl}/relatedbyEntity/tags`,
    getRelatedTagsByObjectId: () => `${this.documentApiUrl}/relatedbyObject/tags`,
    getRelatedByParentDoc: (documentId: number) =>
      `${this.documentApiUrl}/${documentId}/related-child-docs-external`,
    recentDownloads: (): string => `${this.documentApiUrl}/recent-downloads`,
    download: (id: number): string => `${this.documentApiUrl}/${id}/downloadexternal`,
    downloadRelated: (id: number): string => `${this.documentApiUrl}/${id}/downloadrelatedexternal`,
    content: (documentId: number, index: number): string =>
      `${this.documentApiUrl}/${documentId}/content/${index}`,
    preview: (documentId: number, index: number): string =>
      `${this.documentApiUrl}/${documentId}/preview/${index}`,
    metadata: (documentId: number): string => `${this.documentApiUrl}/${documentId}/metadata`,
    getDocumentFormsByCategory: (categoryId: number): string =>
      `${this.documentApiUrlBase}/api/external/documentcategory/${categoryId}/forms`,
    getDocumentForms: (): string => `${this.documentApiUrlBase}/api/external/documentform`,
    getDocumentFormVersion: (id: number): string =>
      `${this.documentApiUrlBase}/api/external/documentform/${id}/versions`,
    token: (): string => this.tokenApiUrl,
    convertToPdf: () => `${this.documentApiUrl}/convert`,
    validateAttachments: () => `${this.documentApiUrl}/validateattachments`,
    _initialize: (config: Environment) => {
      this.documentApiUrlBase = config.baseUrls.documentApiUrl;
      this.documentApiUrl = config.baseUrls.documentApiUrl + '/api/external/document';
      this.tokenApiUrl = config.baseUrls.documentApiUrl + '/api/external/token';
    },
  };

  workGroup = {
    getSystemUsers(id: number): string {
      return `${this.workGroupUrl}/${id}/systemUsers`;
    },
    _initialize(config: Environment) {
      this.workGroupUrl = config.baseUrls.userApiUrl + '/api/external/workgroup';
    },
  };

  urlsForSystemType(typeCode: SystemTypeCode) {
    switch (typeCode) {
      case ObjectTypeCode.PriorAuthorization:
        return this.priorAuthorization;
      case ObjectTypeCode.MedicalBillingDispute:
        return this.medicalBillingDispute;
      case EntityTypeCode.Insurer:
        return this.insurer;
      case EntityTypeCode.ThirdPartyAdministrator:
        return this.thirdPartyAdministrator;
      case EntityTypeCode.MedicalSupplier:
        return this.medicalSupplier;
      case EntityTypeCode.Employer:
        return this.employer;
      case EntityTypeCode.HealthcareProvider:
        return this.healthcareProvider;
      default:
        throw new Error(`No URLs found for type code ${typeCode}.`);
    }
  }

  urlsForTransaction(transactionType: TransactionType) {
    switch (transactionType) {
      case TransactionType.Claim:
        return this.employer;
      default:
        throw new Error(`No URLs found for transaction type ${transactionType}.`);
    }
  }

  sender = {
    getInsurersBySenderNum(senderNum: number) {
      return `${this.senderUrl}/${senderNum}/insurers`;
    },
    getClaimAdminsBySenderNum(senderNum: number, claimAdminEntityId: number) {
      return `${this.insurerUrl}/${senderNum}/${claimAdminEntityId}/senderClaimAdmins`;
    },
    _initialize(config: Environment) {
      this.senderUrl = config.baseUrls.entityApiUrl + '/api/sender';
      this.insurerUrl = config.baseUrls.entityApiUrl + '/api/external/insurer';
    },
  };
}
